// Global Instructions Rule Applied!

import React, { useEffect, useMemo } from 'react'
import { Modal, Form, Input, Select } from 'antd'
import type { CommsTopicGridModel } from '../../../state_manager/data_models/comms_topic_grid_model.ts'

interface AccountRoleChoice {
  id: string
  role: string
}

interface TopicModalProps {
  open: boolean
  topic: CommsTopicGridModel | null
  accountRoleChoices: AccountRoleChoice[]
  confirmLoading: boolean
  onSubmit: (label: string, roleIds: string[]) => void
  onCancel: () => void
}

interface TopicFormValues {
  label: string
  account_role_choice_ids: string[]
}

export const TopicModal: React.FC<TopicModalProps> = ({
  open,
  topic,
  accountRoleChoices,
  confirmLoading,
  onSubmit,
  onCancel
}) => {
  const [form] = Form.useForm<TopicFormValues>()
  const isEdit = Boolean(topic)

  const roleOptions = useMemo(
    () =>
      [...accountRoleChoices]
        .sort((a, b) => a.role.localeCompare(b.role))
        .map((r) => ({ value: r.id, label: r.role })),
    [accountRoleChoices]
  )

  useEffect(() => {
    if (!open) return
    if (topic) {
      form.setFieldsValue({
        label: topic.comms_topics__label,
        account_role_choice_ids: topic.comms_topics__role_ids ?? []
      })
    } else {
      form.resetFields()
    }
  }, [open, topic, form])

  const handleOk = async () => {
    try {
      const values = await form.validateFields()
      onSubmit(values.label.trim(), values.account_role_choice_ids ?? [])
    } catch {
      // validateFields rejects on invalid input; AntD shows the field errors.
    }
  }

  return (
    <Modal
      title={isEdit ? 'Edit Topic' : 'New Topic'}
      open={open}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={confirmLoading}
      okText={isEdit ? 'Save' : 'Create'}
      destroyOnClose
      maskClosable={false}
    >
      <Form form={form} layout='vertical' preserve={false} autoComplete='off'>
        <Form.Item
          name='label'
          label='Topic'
          rules={[
            { required: true, message: 'Topic label is required' },
            { whitespace: true, message: 'Topic label is required' }
          ]}
        >
          <Input placeholder='e.g. Reporting' maxLength={200} allowClear />
        </Form.Item>
        <Form.Item
          name='account_role_choice_ids'
          label='Account roles auto-subscribed to this topic'
          tooltip='Accounts holding any selected role are auto-subscribed; comms-eligible contacts cascade.'
        >
          <Select
            mode='multiple'
            allowClear
            placeholder='Select account roles'
            options={roleOptions}
            optionFilterProp='label'
            showSearch
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default TopicModal
